import { classesApi } from '@/api';
import type {
  IClassDetails,
  IClassStatusObj,
  ISaveAttendance,
  ISaveSummaryComment,
  ISaveSummaryStatus,
  ISaveRemoveStatus,
  IClassUserReschedule,
  IMarkAttendancePayload,
} from '@/api/types';
import { KnownErrors } from '@/store/enums';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type { IClassDetailObj, IClassSummaryObj, IThunkAPI } from '@/store/types';
import { cloneDeep } from '@/helpers';

export const initialState: IClassDetails = {
  classDetails: null,
  classSummary: { summaryList: null, removeList: null, showApplicantDetails: false },
  classStatusList: undefined,
  isClassError: false,
  isClassSuccess: false,
  isClassFetching: false,
  isClassSummaryError: false,
  isClassSummarySuccess: false,
  isClassSummaryFetching: false,
};

export const getClassDetailsByClassId = createAsyncThunk<
  { data: IClassDetailObj; isBackgroundCall: boolean },
  { classId: number; isBackgroundCall?: boolean },
  IThunkAPI
>('get/classDetails/classId', async ({ classId, isBackgroundCall = false }, thunkAPI) => {
  try {
    const response = await classesApi.getClassDetailsById(classId);
    let data = response.data;
    if (response.data === KnownErrors.ItemNotFound) {
      data = null;
    }
    return { data, isBackgroundCall };
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getClassSummaryList = createAsyncThunk<IClassSummaryObj[], { classId: number }, IThunkAPI>(
  'get/classDetails/getClassSummaryList',
  async ({ classId }, thunkAPI) => {
    try {
      const response = await classesApi.getClassSummaryList(classId);
      let data = response.data;
      if (response.data === KnownErrors.ItemNotFound) {
        data = null;
      }
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getClassRemovedList = createAsyncThunk<IClassSummaryObj[], { classId: number }, IThunkAPI>(
  'get/classDetails/getClassRemovedList',
  async ({ classId }, thunkAPI) => {
    try {
      const response = await classesApi.getClassRemovedList(classId);
      let data = response.data;
      if (response.data === KnownErrors.ItemNotFound) {
        data = null;
      }
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getClassUserStatusList = createAsyncThunk<IClassStatusObj[] | undefined, void, IThunkAPI>(
  'get/classDetails/getClassUserStatusList',
  async (_, thunkAPI) => {
    try {
      const response = await classesApi.getClassUserStatusList();
      let data = response.data;
      if (response.data === KnownErrors.ItemNotFound) {
        data = undefined;
      }
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setAttendance = createAsyncThunk<unknown, ISaveAttendance[], IThunkAPI>(
  'set/classId/hasAttendedSession',
  async (attendencesArray, thunkAPI) => {
    try {
      const response = await classesApi.setAttendance(attendencesArray);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setSummaryComments = createAsyncThunk<unknown, ISaveSummaryComment, IThunkAPI>(
  'set/summary/comments',
  async ({ applicationId, userId, classId, comments }, thunkAPI) => {
    try {
      const response = await classesApi.setSummaryComments({ applicationId, userId, classId, comments });
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setSummaryStatus = createAsyncThunk<any, ISaveSummaryStatus[], IThunkAPI>(
  'set/summary/status',
  async (summaryStatusLists, thunkAPI) => {
    try {
      const response = await classesApi.setSummaryStatus(summaryStatusLists);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setRemoveStatus = createAsyncThunk<any, ISaveRemoveStatus, IThunkAPI>(
  'set/summary/status',
  async ({ applicationId, userId, classId, status, currentApplicationStatusId }, thunkAPI) => {
    try {
      const response = await classesApi.setRemoveStatus({
        applicationId,
        userId,
        classId,
        status,
        currentApplicationStatusId,
      });
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const registerRescheduleClass = createAsyncThunk<any, IClassUserReschedule, IThunkAPI>(
  'SP/classuser/reschedule/save',
  async (payload, thunkAPI) => {
    try {
      const response = await classesApi.registerRescheduleClass(payload);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const markAttendance = createAsyncThunk<any, IMarkAttendancePayload, IThunkAPI>(
  'SP/classuser/markAttendance',
  async (payload, thunkAPI) => {
    try {
      const response = await classesApi.markAttendance(payload);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

const transformClassDetails = (classDetails: IClassDetailObj) => {
  const clonedClassDetails: IClassDetailObj = cloneDeep(classDetails);

  const updatedClassSessionList = clonedClassDetails?.classSessionList?.map(classSession => {
    return {
      ...classSession,
      showApplicantDetails: false,
    };
  });
  clonedClassDetails.classSessionList = updatedClassSessionList;
  return clonedClassDetails;
};

export const removeFromClassInitiatedBySP = createAsyncThunk<any, ISaveSummaryStatus, IThunkAPI>(
  'SP/classuser/remove/class',
  async (classUserStatus, thunkAPI) => {
    try {
      const response = await classesApi.removeFromClassInitiatedBySP(classUserStatus);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const classDetailsSlice = createSlice({
  name: 'classDetails',
  initialState,
  reducers: {
    clearClassDetails(state) {
      state.classDetails = null;
    },
    updateClassDetailApplicantDetails(state, { payload }) {
      const { timeTableId, showApplicantDetails, selectedApplicationId = null } = payload;
      const classDetailsLength = state.classDetails?.classSessionList?.length || 0;
      if (state.classDetails) {
        for (let i = 0; i < classDetailsLength; i++) {
          if (state.classDetails?.classSessionList[i].timeTableId === timeTableId) {
            state.classDetails.classSessionList[i].showApplicantDetails = showApplicantDetails;
            state.classDetails.classSessionList[i].selectedApplicationId = selectedApplicationId;
            break;
          }
        }
      }
    },
    updateSummaryApplicantDetails(state, { payload }) {
      const { showSmryApplicantDetails, selectedApplicationId = null } = payload;
      state.classSummary.showApplicantDetails = showSmryApplicantDetails;
      state.classSummary.selectedApplicationId = selectedApplicationId;
    },
    updateClassDetailApplicantAttendance(state, { payload }) {
      const { classId, userId, timeTableId, hasAttendedSession } = payload;
      const classDetailsLength = state.classDetails?.classSessionList?.length || 0;
      const summaryListLength = state.classSummary?.summaryList?.length || 0;
      if (state.classDetails && state?.classDetails?.classId === classId) {
        outerloop: for (let i = 0; i < classDetailsLength; i++) {
          if (state.classDetails?.classSessionList[i].timeTableId === timeTableId) {
            const userAttendanceListLength = state.classDetails?.classSessionList?.[i]?.userAttendanceList?.length || 0;
            for (let j = 0; j < userAttendanceListLength; j++) {
              if (state.classDetails?.classSessionList[i].userAttendanceList[j].userId === userId) {
                state.classDetails.classSessionList[i].userAttendanceList[j].hasAttendedSession = hasAttendedSession;
                break outerloop;
              }
            }
          }
        }
      }
      if (summaryListLength) {
        for (let k = 0; k < summaryListLength; k++) {
          if (state.classSummary.summaryList && state.classSummary.summaryList[k].userId === userId) {
            if (hasAttendedSession) {
              ++state.classSummary.summaryList[k].sessionsAttended;
            } else {
              --state.classSummary.summaryList[k].sessionsAttended;
            }
            break;
          }
        }
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getClassDetailsByClassId.fulfilled, (state, action) => {
        state.classDetails = transformClassDetails(action?.payload?.data);
        state.isClassSuccess = true;
        state.isClassError = false;
        state.isClassFetching = false;
      })
      .addCase(getClassDetailsByClassId.rejected, (state, action) => {
        if (!action.meta.arg?.isBackgroundCall) {
          state.isClassError = true;
          state.isClassSuccess = false;
          state.isClassFetching = false;
        }
      })
      .addCase(getClassDetailsByClassId.pending, (state, action) => {
        if (!action.meta.arg?.isBackgroundCall) {
          state.isClassFetching = true;
        }
      })
      .addCase(getClassSummaryList.fulfilled, (state, action) => {
        state.classSummary = { ...state.classSummary, summaryList: action?.payload, showApplicantDetails: false };
        state.isClassSummarySuccess = true;
        state.isClassSummaryError = false;
        state.isClassSummaryFetching = false;
      })
      .addCase(getClassSummaryList.rejected, state => {
        state.isClassSummaryError = true;
        state.isClassSummarySuccess = false;
        state.isClassSummaryFetching = false;
      })
      .addCase(getClassSummaryList.pending, state => {
        state.isClassSummaryFetching = true;
      })
      .addCase(getClassRemovedList.fulfilled, (state, action) => {
        state.classSummary = { ...state.classSummary, removeList: action?.payload, showApplicantDetails: false };
        state.isClassSummarySuccess = true;
        state.isClassSummaryError = false;
        state.isClassSummaryFetching = false;
      })
      .addCase(getClassRemovedList.rejected, state => {
        state.isClassSummaryError = true;
        state.isClassSummarySuccess = false;
        state.isClassSummaryFetching = false;
      })
      .addCase(getClassRemovedList.pending, state => {
        state.isClassSummaryFetching = true;
      })
      .addCase(getClassUserStatusList.fulfilled, (state, action) => {
        state.classStatusList = action?.payload;
      });
  },
});

// actions
export const {
  clearClassDetails,
  updateClassDetailApplicantDetails,
  updateSummaryApplicantDetails,
  updateClassDetailApplicantAttendance,
} = classDetailsSlice.actions;

// selectors
export const classDetailsSelector = (state: RootState): IClassDetails['classDetails'] =>
  state.classDetails.classDetails;
export const classSummarySelector = (state: RootState): IClassDetails['classSummary'] =>
  state.classDetails.classSummary;
export const classSummarylistSelector = (state: RootState): IClassSummaryObj[] =>
  state.classDetails.classSummary.summaryList || [];
export const classSummaryRemovelistSelector = (state: RootState): IClassSummaryObj[] =>
  state.classDetails.classSummary.removeList || [];

export const isClassDetailsFetching = (state: RootState): boolean => state.classDetails.isClassFetching;
export const isClassSummaryFetching = (state: RootState): boolean => state.classDetails.isClassSummaryFetching;
export const classStatusListSelector = (state: RootState): IClassStatusObj[] =>
  state.classDetails?.classStatusList ?? [];

export default classDetailsSlice.reducer;
